//
//  Accessibility
//
//  This comp includes the Accessibility Lightbox and the button that other screens
//  use to display that lightbox.
//  The lightbox allows the user to determine if ADA colorsensitive mode is active
//  or not.
//  if in localhost, the screen includes a quick theme switcher.

import React, { useState, useEffect, useCallback } from 'react';
import { Checkbox, Dialog, Button } from '@framework-controls/core';
import { strings } from 'src/localization';
import { IconAccessibility, IconRemove } from 'src/content/icons';
import { checkSpecTheme } from 'src/helpers/OEMHelper/OEMHelper';
import SVPButton from '../SVPButtons/SVPButton';
import * as css from './Accessibility.module.scss';
import { VehicleRec, ascCtaInteractionInput, ascPageviewInput, ascElementConfigurationInput } from 'src/models';
import { ascCtaInteraction, ascPageview, getElementColor, ascElementConfiguration } from 'src/helpers/EventHelper/EventHelper';
import classNames from 'classnames';

export interface IProps {
    theme: string;
    specTheme?: string;
    docSpecTheme?: string;
    vehicles: VehicleRec[];
    selectedVehicle?: string;
    updateTheme: (varTheme: string) => void;
}

const Accessibility = (props: IProps) => {
    // Flag to determine if the Dialog modal should be displayed(open) or not
    const [openFlag, setOpenFlag] = useState(false);
    // Flag used to determine if the Color sensitive checkbox is checked or not
    const [colorCkbFlag, setColorCkbFlag] = useState(false);
    // Set the Color sensitive checkbox as true(checked) if the current color theme is colorSensitive
    const [startingCheck, setStartingCheck] = useState(false);
    // Set startingCheck to remember the checkbox setting before any user changes
    useEffect(() => {
        if (props.theme === 'colorSensitive') {
            setColorCkbFlag(true);
            setStartingCheck(true);
        } else {
            setStartingCheck(false);
        }
    }, [props.theme, setColorCkbFlag]);

    useEffect(() => {
        if (openFlag) {
            const ascPageViewInp: ascPageviewInput = {
                pageType: 'service',
                errorCode: '',
                itemResults: '',
            };
            ascPageview(ascPageViewInp, props.vehicles, props.selectedVehicle);
        }
    }, [props.selectedVehicle, props.vehicles, openFlag]);

    // When the checkbox is toggled, change the current value of the ColorCbkflag.
    const onToggle = useCallback(() => {
        setColorCkbFlag(!colorCkbFlag);
    }, [colorCkbFlag]);

    const checkboxEventHandler = useCallback((e: any) => {
        onToggle();
        const elementColor = getElementColor(e, 'fill');
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.ColorSensitive,
            elementColor: elementColor,
            elementOrder: '',
            elementType: 'accessibility_tool',
            elementSubType: 'checkbox',
            eventAction: 'click',
            eventActionResult: colorCkbFlag ? 'uncheck' : 'check',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
    }, [props.vehicles, props.selectedVehicle, colorCkbFlag, onToggle]);
    // When the user clicks outside of the dialog or hits the cancel button in the header, do not save changes.
    const resetCheckbox = useCallback((e: any) => {
        if (startingCheck !== colorCkbFlag) {
            setColorCkbFlag(startingCheck);
        }
        setOpenFlag(false);
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: '',
            elementColor: '',
            elementOrder: '',
            elementType: 'accessibility_tool',
            elementSubType: 'nav_button',
            eventAction: 'click',
            eventActionResult: 'close',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
    }, [colorCkbFlag, startingCheck, props.vehicles, props.selectedVehicle]);

    // When update button is pressed, change the active color theme based on the current value of the ColorCkbFlag
    // Set the users option to the Cookie
    // Then close the Dialog modal
    const updateHandler = useCallback((e: any) => {
        if (colorCkbFlag) {
            props.updateTheme('colorSensitive');
            document.cookie = 'colorSensitive=colorSensitive;SameSite=None;secure';
        } else if (props.specTheme) {
            props.updateTheme(checkSpecTheme(props.specTheme));
            document.cookie = `colorSensitive=${props.specTheme};SameSite=None;secure`;
        } else if (props.docSpecTheme) {
            props.updateTheme(checkSpecTheme(props.docSpecTheme));
            document.cookie = `colorSensitive=${props.docSpecTheme};SameSite=None;secure`;
        }
        setOpenFlag(false);
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.UpdateSite,
            elementColor: '',
            elementOrder: '',
            elementType: 'accessibility_tool',
            elementSubType: 'other_buttons',
            eventAction: 'click',
            eventActionResult: 'close',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
        const ascElementConfigurationInp: ascElementConfigurationInput = {
            pageType: 'service',
            elementText: strings.UpdateSite,
            elementType: 'item_details',
            elementColor: '',
            eventAction: 'click',
            eventActionResult: 'close',
            linkUrl: '',
        };
        ascElementConfiguration(ascElementConfigurationInp, e);
    }, [props.updateTheme, colorCkbFlag, props.specTheme, props.docSpecTheme, props.vehicles, props.selectedVehicle]);

    const accessiblityOpenHandler = useCallback((e: any) => {
        setOpenFlag(true);

        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.ACSTooltip,
            elementColor: '',
            elementOrder: '',
            elementType: 'accessibility_tool',
            elementSubType: 'nav_buttons',
            eventAction: 'click',
            eventActionResult: 'open',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
    }, [props.vehicles, props.selectedVehicle]);

    // set the css class name to be used by the checkbox based on if it is checked
    let variant = 'primaryCkb';
    if (colorCkbFlag) {
        variant = 'primaryCkbChecked';
    }

    const headerBoxStyles = classNames(
        css.headerbox,
        {
            [css.headerboxMazda]: props.theme === 'mazda',
            [css.headerboxGenesis]: props.theme === 'genesis',
        },
    );

    return (
        <div className={css.baseline}>
            <Button
                id='accessIcon'
                onClick={accessiblityOpenHandler}
                tooltip={strings.ACSTooltip}
                variant={'help'}>
                <div className={css.accessiblity}>
                    <IconAccessibility />
                </div>
            </Button>
            <Dialog
                data-testid = 'ascDialog'
                cssClasses={{
                    content: css.content,
                    paper: css.paper,
                    header: css.LightBoxHeader,
                }}
                isOpen={openFlag}
                anchorOrigin={{
                    vertical: 'top',
                }}
                header={strings.AccessiblityPanel}
                hideSave={true}
                hideCancel={true}
                disableUpperCase={true}
                onCancel={resetCheckbox}
                onBackDropClick={resetCheckbox}
                width={'full'}
            >
                <div>
                    <div className={headerBoxStyles}>
                        {strings.AccessiblityPanel}
                        <div className={css.headerIcon}>
                            <IconRemove id='closeBtn' onClick={resetCheckbox}/>
                        </div>
                    </div>
                    <div className={css.header}>
                        <h4 id='h4LightBox'>{strings.AccessiblityHeader}</h4>
                    </div>
                    <div
                        id='divCheckbox'
                        onClick={checkboxEventHandler}
                        className={css.checkboxRow}>
                        <Checkbox
                            id='colorCkb'
                            size={'rg'}
                            variant={variant}
                            checked={colorCkbFlag}
                            onChange={onToggle}>
                        </Checkbox>
                        <div className={css.checkboxLit}>{strings.ColorSensitive}</div>
                    </div>
                    <hr></hr>
                    <div className={css.update}>
                        <SVPButton
                            id='updateSiteBtn'
                            message={strings.UpdateSite}
                            noArrow={true}
                            handler={updateHandler}
                            theme={props.theme}/>
                    </div>
                </div>
            </Dialog>
        </div>
    );
};

export default Accessibility;
